// Multivariate Dependencies
import React, { lazy, useEffect, useCallback, useMemo, useState, useReducer, useRef } from 'react';
// Components
var DropdownListContainer = lazy(function () {
    /* webpackChunkName: "DropdownListContainer" */
    return import('./DropdownListContainer');
});
var DropdownErrorContainer = lazy(function () {
    /* webpackChunkName: "DropdownErrorContainer" */
    return import('../styles/StyledDropdownErrorContainer');
});
import { PopupMechanism } from '../../../common/components/PopupMechanism';
import { Container } from '../../../common/styles/container';
import { DropdownInputContainer } from './DropdownInputContainer';
import { SuspenseWrapper } from '../../../common/components/SuspenseWrapper';
// Utils
import { closeListAction } from '../utils/closeListAction';
import { dropdownReducer } from '../utils/dropdownReducer';
import { handleSelectOption } from '../utils/handleSelectOption';
import { selectDropdownOptionAction } from '../utils/selectDropdownOptionAction';
import { getColourDeciderGivenAnError } from '../utils/getColourDeciderGivenAnError';
import { DropdownStates } from '../types/DropdownStates';
// Hooks
import { useTooltip } from '../../../legacy/TooltipLegacy/hooks/useTooltip';
import { useTheme } from 'styled-components';
// Lazy loaded components
/**
 * The dropdown reusable component. Can be used alone if a tooltip is not required.
 * If a tooltip is required, utilise DropdownWithTooltip or pass through a custom element to subscribedTooltipEventElement
 *
 * @example
 *  <Dropdown
 *    // Forces the state of the dropdown, in this scenario, the dropdown will render as 'locked'
 *    initialDropdownState={DropdownStates.LOCKED}
 *
 *    // Populates the dropdown with a single value that is selected. This will also be added to the dropdown options
 *    // so ensure the icon is set (otherwise you will see this option in the menu without the icon)
 *    initialDropdownOption={{
 *      id: 1,
 *      label: 'Initial value',
 *      icon: <FaRocket />,
 *      payload: {
 *        unique: 'data'
 *      }
 *    }}
 *
 *    // An optional ref that will enable tooltip usage. Use DropdownWithTooltip component instead (or pass in a custom element ref.current
 *    // if you would rather reuse an existing element)
 *    subscribedTooltipEventElement={}
 *
 *    // Callback when a dropdown option is selected
 *    onDropdownOptionSelected={(dropdownOption: DropdownOption) => doSomething(dropdownOption)}
 *
 *    // Callback when the dropdown is cleared (the X icon is clicked, backspace or delete is pressed on the text input field)
 *    onDropdownOptionCleared={() => doSomething()}
 *
 *    // Called when loading the dropdown menu.
 *    // Allow the user to fetch data and correctly format the payload to be loaded in the dropdown.
 *    // Data MUST contain an id and label. The icon is option. Payload can be anything and will be accessible in onDropdownOptionSelected
 *    getDropdownOptions={(searchValue: string) => doHttpRequestAndFormatResponse(searchValue)}
 *
 *    // Called when the tooltip is ready to open.
 *    // Returning JSX will render the JSX to the tooltip container, If nothing is returned then the tooltip will not render
 *    getTooltipContent={(dropdownOption?: DropdownOption) => <div>My tooltip</div>}
 *
 *    // Callback when the enter key is pressed while the focus is on the dropdown text input field AND the dropdown menu is closed
 *    handleEnterOnInput={() => doSomething()}
 *
 *    // Callback when the user has double clicked on the dropdown text input field. Can be used to open a new tab with the dropdown option data
 *    handleDoubleClickOnInput={(dropdownOption?: DropdownOption) => doSomething(dropdownOption)}
 *
 *    // If this is set to anything other than null or undefined, the entire dropdown will render with error colours
 *    // This property is to be handled by the user. Dropdown will never automatically remove errors
 *    errorMessage='This is an optional error'
 *  />
 * @param {DropdownBaseProps} {
 *   initialDropdownState,
 *   initialDropdownOption,
 *   subscribedTooltipEventElement,
 *   onDropdownOptionSelected,
 *   onDropdownOptionCleared,
 *   getDropdownOptions,
 *   getTooltipContent,
 *   handleEnterOnInput,
 *   handleDoubleClickOnInput,
 *   errorMessage
 * }
 * @return {JSX.Element}
 */
export var DropdownBase = function (_a) {
    var initialDropdownState = _a.initialDropdownState, initialDropdownOption = _a.initialDropdownOption, subscribedTooltipEventElement = _a.subscribedTooltipEventElement, onDropdownOptionSelected = _a.onDropdownOptionSelected, onDropdownOptionCleared = _a.onDropdownOptionCleared, getDropdownOptions = _a.getDropdownOptions, getTooltipContent = _a.getTooltipContent, handleEnterOnInput = _a.handleEnterOnInput, handleDoubleClickOnInput = _a.handleDoubleClickOnInput, errorMessage = _a.errorMessage, CustomDropdownListOption = _a.CustomDropdownListOption;
    /**
     * Dropdown option and dropdown menu management
     */
    var _b = useReducer(dropdownReducer, {
        dropdownState: initialDropdownState || DropdownStates.LIST_CLOSED,
        dropdownOptions: initialDropdownOption ? [initialDropdownOption] : [],
        selectedDropdownOption: initialDropdownOption || null,
        highlightedOptionIndex: 0,
    }), _c = _b[0], dropdownState = _c.dropdownState, dropdownOptions = _c.dropdownOptions, selectedDropdownOption = _c.selectedDropdownOption, highlightedOptionIndex = _c.highlightedOptionIndex, dispatch = _b[1];
    /**
     * styling
     */
    var themeStyles = useTheme();
    var getEitherDefaultOrErrorPrimaryColour = getColourDeciderGivenAnError(errorMessage)(themeStyles.contentNegative);
    var getEitherDefaultOrErrorSecondaryColour = getColourDeciderGivenAnError(errorMessage)(themeStyles.backgroundUI);
    /**
     * Tooltip events
     */
    var _d = useTooltip(subscribedTooltipEventElement), openTooltip = _d.openTooltip, closeTooltip = _d.closeTooltip;
    /**
     * HTML elements and utils
     */
    var _e = useState(), dropdownInputElement = _e[0], setDropdownInputElement = _e[1];
    var dropdownContainerRef = useRef();
    var dropdownListContainerRef = useRef();
    var setDropdownInputElementRef = useCallback(function (ref) {
        ref && setDropdownInputElement(ref);
    }, []);
    var focusOnTheDropdownInput = useMemo(function () { return function () { return dropdownInputElement === null || dropdownInputElement === void 0 ? void 0 : dropdownInputElement.focus(); }; }, [dropdownInputElement]);
    /**
     * Setting selected dropdown options
     */
    useEffect(function () {
        if ((initialDropdownOption === null || initialDropdownOption === void 0 ? void 0 : initialDropdownOption.id) !== undefined) {
            dispatch(selectDropdownOptionAction(initialDropdownOption));
        }
    }, [initialDropdownOption]);
    var setSelectedDropdownOption = useMemo(function () { return handleSelectOption(onDropdownOptionSelected, focusOnTheDropdownInput, dispatch); }, [onDropdownOptionSelected, focusOnTheDropdownInput]);
    // close the dropdown list on scroll (to prevent list from hanging)
    useEffect(function () {
        var closeDropdown = function (event) {
            var withBoundaries = event.composedPath();
            if (event.target !== dropdownListContainerRef.current &&
                !withBoundaries.includes(dropdownContainerRef.current)) {
                dispatch(closeListAction);
            }
        };
        document.addEventListener('scroll', closeDropdown, { capture: true });
        document.addEventListener('click', closeDropdown);
        return function () {
            document.removeEventListener('scroll', closeDropdown, { capture: true });
            document.removeEventListener('click', closeDropdown);
        };
    }, []);
    return (React.createElement(Container, { display: 'block', width: '100%', height: '100%', position: 'relative' },
        React.createElement(DropdownInputContainer, { dropdownContainerRef: dropdownContainerRef, ref: setDropdownInputElementRef, dropdownState: dropdownState, dropdownOptions: dropdownOptions, selectedDropdownOption: selectedDropdownOption, highlightedOptionIndex: highlightedOptionIndex, dispatch: dispatch, handleDoubleClickOnInput: handleDoubleClickOnInput, onDropdownOptionCleared: onDropdownOptionCleared, onDropdownOptionSelected: onDropdownOptionSelected, getDropdownOptions: getDropdownOptions, handleEnterOnInput: handleEnterOnInput, getEitherDefaultOrErrorPrimaryColour: getEitherDefaultOrErrorPrimaryColour, getEitherDefaultOrErrorSecondaryColour: getEitherDefaultOrErrorSecondaryColour, focusOnTheDropdownInput: focusOnTheDropdownInput, getTooltipContent: getTooltipContent, openTooltip: openTooltip, closeTooltip: closeTooltip, setSelectedDropdownOption: setSelectedDropdownOption }),
        dropdownState === DropdownStates.LIST_OPEN && (React.createElement(SuspenseWrapper, { fallback: React.createElement(React.Fragment, null) },
            React.createElement(PopupMechanism, { targetedElement: dropdownContainerRef, placement: 'bottom', alignToTargetedElement: 'left', shouldInheritWidth: true },
                React.createElement(DropdownListContainer, { ref: dropdownListContainerRef, dropdownOptions: dropdownOptions, highlightedOptionIndex: highlightedOptionIndex, dispatch: dispatch, onSelectOption: setSelectedDropdownOption, openTooltip: openTooltip, closeTooltip: closeTooltip, getTooltipContent: getTooltipContent, CustomDropdownListOption: CustomDropdownListOption })))),
        errorMessage !== undefined && errorMessage !== null && (React.createElement(SuspenseWrapper, { fallback: React.createElement(React.Fragment, null) },
            React.createElement(DropdownErrorContainer, { errorMessage: errorMessage, getEitherDefaultOrErrorPrimaryColour: getEitherDefaultOrErrorPrimaryColour })))));
};
//# sourceMappingURL=DropdownBase.js.map